import { createApi } from "@reduxjs/toolkit/query/react"
import axiosBaseQuery from "../../../axiosBaseQuery"
import { PROVISIONING_ENDPOINTS } from "../../../../api/constants"
import { mutateWithCsrf } from "../../../../api/csrfMutation"

/**
 * RTK Query API провижининга — ОПЕРАТОРСКАЯ плоскость портала хаба (`IsHubAdmin`).
 *
 * Экран отвечает на два вопроса: «что сделать с заказом, который встал» и «как
 * подключить клинику под ключ». Второй появился здесь вынужденно: авторство заказа по
 * замыслу принадлежит коммерческому слою (заказ — след оплаты), но EyeStore ещё не
 * существует, а машинный эндпоинт человеку недоступен — то есть подключить клинику из
 * интерфейса было нельзя вообще. До появления EyeStore заказы заводит оператор.
 *
 * Контракт строки списка (`GET admin/provisioning/orders/`):
 * {id, order_id, kind, status, organization_id, organization_slug, organization_name,
 *  failed_step, last_error, retryable, running, secrets_issued_at, created_at, updated_at}.
 * Деталь добавляет: {site_id, license_id, incident_ticket_id, deploy_token_username,
 *  broker_client_id, idp_alias, desired_state, shop_order_url}.
 *
 * СУММ И ПЛАТЕЖЕЙ В КОНТРАКТЕ НЕТ ФИЗИЧЕСКИ: платёжные данные в хаб не попадают,
 * они живут в EyeStore (граница среза). Ссылка `shop_order_url` — единственный мост,
 * и она пустая, если шаблон не сконфигурирован.
 *
 * СЕКРЕТОВ В ОТВЕТАХ НЕТ — кроме ответа на СОЗДАНИЕ. Одноразовые enrollment- и
 * deploy-токен §7.4 выдаются ровно одному вызову, тому, который заказ исполнил, и
 * другого момента их показать не существует. Повтор того же `order_id` их уже не
 * отдаёт (`secrets_already_issued`). Поэтому ответ `createProvisioningOrder` НЕ
 * кешируется под тегом и не переиспользуется: значение живёт только в UI, показавшем
 * его оператору.
 *
 * `retry` пересоздаёт одноразовые секреты, но НЕ отдаёт их: повторная выдача —
 * отдельная аудируемая операция перевыпуска (§7.6). Ответ сообщает об этом флагом
 * `secrets_reissue_required` — молчать нельзя, иначе оператор счёл бы заказ
 * завершённым, а клиника не смогла бы завершить enrollment.
 *
 * Списки — UnifiedOffsetPagination: страница приезжает под ключом `results` (не `items`).
 */
export const provisioningApi = createApi({
  reducerPath: "provisioningApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["ProvisioningOrder"],
  endpoints: builder => ({
    selectProvisioningInstallerBundle: builder.mutation({
      async queryFn({ id, body }, api) {
        const user = api.getState().auth?.user
        const current = () => Boolean(user) && !api.signal.aborted && api.getState().auth?.user === user
        try {
          const data = await mutateWithCsrf({
            url: `${PROVISIONING_ENDPOINTS.ORDER_DETAIL(id)}installer-bundle/`,
            csrfUrl: `${PROVISIONING_ENDPOINTS.ORDERS}csrf/`,
            data: body, status: 200, current,
          })
          const signedFields = ["bundle_descriptor_url", "bundle_signature_url", "bundle_image_recipe_version", "bundle_channel", "bundle_image_manifest_sha256"]
          const signed = (body.bundle_version || "").toLowerCase().includes("-installer") || signedFields.some(key => Boolean(body[key]))
          if (!data || data.id !== id || data.organization_id !== body.organization_id ||
              data.license_id !== body.license_id || data.installer_bundle_selected !== true ||
              typeof data.changed !== "boolean" ||
              ["bundle_url", "bundle_version", "bundle_sha256"].some(key => data[key] !== body[key]) ||
              (signed && (data.bundle_signed !== true || signedFields.some(key => data[key] !== body[key])))) {
            throw new Error("HUB не подтвердил выбранный комплект этого заказа")
          }
          return { data }
        } catch (error) {
          return { error: { status: error.response?.status || "CUSTOM_ERROR", data: error.data || { message: error.message } } }
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: "ProvisioningOrder", id }],
    }),
    // Список заказов: фильтры status/kind/organization, поиск по order_id и слагу
    // организации, сортировка created_at/updated_at/status.
    getProvisioningOrders: builder.query({
      query: ({
        limit = 20,
        offset = 0,
        search,
        status,
        kind,
        organization,
        ordering,
      } = {}) => ({
        url: PROVISIONING_ENDPOINTS.ORDERS,
        method: "GET",
        params: {
          limit,
          offset,
          ...(search ? { search } : {}),
          ...(status ? { status } : {}),
          ...(kind ? { kind } : {}),
          ...(organization ? { organization } : {}),
          ...(ordering ? { ordering } : {}),
        },
      }),
      providesTags: [{ type: "ProvisioningOrder", id: "LIST" }],
    }),

    // Создание и НЕМЕДЛЕННОЕ исполнение заказа (подключение клиники «под ключ»).
    //
    // Тело — желаемый состав §7.4: {kind, organization{slug,name}, site{name,city},
    // org_admin{email}, license{license_type,valid_until?}, modules[]}. Платёжных
    // полей в нём нет и быть не может: платёжные данные в хаб не попадают.
    //
    // `order_id` с клиента НЕ отправляется: ключ идемпотентности выпускает сервер.
    // Придумывать его в браузере значит либо просить оператора набрать UUID руками,
    // либо генерировать новый на каждый рендер — и тогда повтор после сетевой ошибки
    // завёл бы ВТОРУЮ организацию вместо повтора того же заказа.
    //
    // Инвалидируем список: заказ уже исполнен, и таблица обязана это показать.
    createProvisioningOrder: builder.mutation({
      query: body => ({
        url: PROVISIONING_ENDPOINTS.ORDERS,
        method: "POST",
        data: body,
      }),
      invalidatesTags: [{ type: "ProvisioningOrder", id: "LIST" }],
    }),

    // Деталь: ссылки на созданное, инцидент и desired_state после scrub_secrets.
    getProvisioningOrder: builder.query({
      query: id => ({
        url: PROVISIONING_ENDPOINTS.ORDER_DETAIL(id),
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "ProvisioningOrder", id }],
    }),

    // Перезапуск застрявшего заказа. Тела нет: перезапускается ИМЕННО тот
    // desired_state, что хранится в хабе, — иначе оператор мог бы подменить заказ,
    // и ключ идемпотентности order_id перестал бы что-либо означать.
    // Инвалидируем и деталь, и список: retry меняет статус и failed_step.
    retryProvisioningOrder: builder.mutation({
      query: id => ({
        url: PROVISIONING_ENDPOINTS.ORDER_RETRY(id),
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "ProvisioningOrder", id },
        { type: "ProvisioningOrder", id: "LIST" },
      ],
    }),

    // Перевыпуск одноразовых секретов (§7.6). Второе и последнее место контракта, где
    // секреты приезжают в браузер, — и единственный выход из ситуации «покупатель
    // потерял токен» или «retry их пересоздал и не отдал». До появления этой кнопки
    // выход был один: передать секреты руками, мимо аудита.
    //
    // Ответ, как и у создания, НЕ кешируется под тегом: значение живёт только в том
    // компоненте, который показал его оператору. Кеш RTK Query переживает уход со
    // страницы, и одноразовый секрет в нём означал бы, что «один раз» — неправда.
    //
    // Инвалидируется деталь и список: меняются secrets_issued_at и
    // deploy_token_username, а список показывает их в строке.
    reissueProvisioningOrderSecrets: builder.mutation({
      query: id => ({
        url: PROVISIONING_ENDPOINTS.ORDER_REISSUE_SECRETS(id),
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "ProvisioningOrder", id },
        { type: "ProvisioningOrder", id: "LIST" },
      ],
    }),
  }),
})

export const {
  useGetProvisioningOrdersQuery,
  useGetProvisioningOrderQuery,
  useCreateProvisioningOrderMutation,
  useRetryProvisioningOrderMutation,
  useReissueProvisioningOrderSecretsMutation,
  useSelectProvisioningInstallerBundleMutation,
} = provisioningApi

export default provisioningApi
